import { CustomException } from "types/customException";

const apiUrl = process.env.REACT_APP_API_URL;

type RequestOptions = {
	contentType?:
		| "application/json"
		| "multipart/form-data"
		| "application/octet-stream"
		| "text/plain"
		| "application/x-www-form-urlencoded";
	headers?: Record<string, string>;
	body?: any;
};

export interface ApiResponse<T> {
	success: boolean;
	statusCode: number;
	messages: { errors: MessagesContent[]; info: MessagesContent[] };
	data: T;
}

export interface PagedResponse<T> {
	items: T[];
	pagedNumber: number;
	pageSize: number;
	totalItems: number;
}

interface MessagesContent {
	description: string;
	friendlyMessageKey: string;
	keys: Record<string, string>;
}

export class ApiClient {
	private baseURL: string = apiUrl!;

	private async request<T>(
		url: string,
		method: string,
		options?: RequestOptions
	): Promise<ApiResponse<T>> {
		// Getting token from the localStorage
		/*const token = localStorage.getItem("token");
		if (!token) {
			console.error("Token no encontrado en el localStorage.");
			throw new Error("Token no encontrado");
		}*/

		// Adding token and options to headers
		const headers: Record<string, string> = {
			//Authorization: `Bearer ${token}`,
			...(options?.headers || {}),
		};

		// If options contains contentType, then add it to headers
		if (options?.contentType) {
			headers["Content-Type"] = options.contentType ?? "application/json";
		} else {
			headers["Content-Type"] = "application/json";
		}

		// Setting config: method, headers and body if it's not a GET or HEAD method
		const config: RequestInit = {
			method,
			headers,
			body:
				options?.body && method !== "GET" && method !== "HEAD"
					? options?.body
					: undefined,
		};

		try {
			// Fetching and getting content type
			const response = await fetch(`${this.baseURL}${url}`, config);
			const contentType = response.headers.get("Content-Type");

			if (!response.ok) {
				let responseBody;

				if (contentType && contentType.includes("application/json")) {
					responseBody = await response.json();
				} else {
					responseBody = await response.text();
				}

				if (!responseBody.success && responseBody.messages)
					throw new CustomException(
						responseBody.messages.errors[0].message,
						responseBody.statusCode,
						responseBody.messages.errors[0].friendlyMessageKey,
						responseBody.messages.errors[0].keys
					);

				throw new Error(
					`${response.status} - ${response.statusText} - ${
						responseBody || "An error occurred"
					}`
				);
			}

			/*
			// Verifying if the response doesn't have content
			if (
				response.status === 204 ||
				response.headers.get("Content-Length") === "0"
			) {
				return null as T;
			}*/

			// Verifying the type of content of the response
			let responseBody;
			if (contentType?.includes("application/json")) {
				responseBody = await response.json();
			} else if (contentType?.includes("text/plain")) {
				responseBody = await response.text();
			}

			// If the contentType is not recognized, return the crude response
			return responseBody;
		} catch (error) {
			if (error instanceof CustomException) {
				console.error(error.toJSON());
				throw error;
			} else {
				console.error(error);
				throw new Error(`Fetch error: ${(error as Error).message}`);
			}
		}
	}

	public async get<T>(
		url: string,
		options?: RequestOptions
	): Promise<ApiResponse<T>> {
		return this.request<T>(url, "GET", options);
	}

	public async post<T>(
		url: string,
		body: any,
		options?: RequestOptions
	): Promise<ApiResponse<T>> {
		return this.request<T>(url, "POST", { ...options, body });
	}

	public async put<T>(
		url: string,
		body: any,
		options?: RequestOptions
	): Promise<ApiResponse<T>> {
		return this.request<T>(url, "PUT", { ...options, body });
	}

	public async delete<T>(
		url: string,
		options?: RequestOptions
	): Promise<ApiResponse<T>> {
		return this.request<T>(url, "DELETE", options);
	}
}
